import { useState } from 'react';
import {
  CYCLE_LABEL,
  CURRENCIES,
  VAT_RATES,
  formatMoney,
  parseMoneyToCents,
  periodMoney,
  vatLabel,
} from '@shared/money';
import {
  EMPLOYER_SHARE_PERCENT,
  FIXED_CATEGORIES,
  FIXED_STATUS_LABEL,
  GEWERBESTEUER_TERMINE,
  ICON_LABEL,
  ICON_SYMBOLS,
  PAYMENT_LABEL,
  type FixedCost,
  type FixedCostStatus,
  type IconSymbol,
  type PaymentMethod,
  type PayrollPosition,
  payrollTotal,
} from '@shared/types';
import { useVault } from '../lib/vault';
import { api, type FixedCostInput } from '../lib/api';
import { plural } from '@shared/text';
import { ErrorNote, Modal, useAction } from './ui';
import { ItemIcon, SymbolIcon } from './ItemIcon';

/**
 * Erkennt Lohnposten an der Kategorie. Die Umlaute muessen mit hinein:
 * "Löhne & Gehälter" enthaelt weder "lohn" noch "gehalt" - das ö und das ä
 * sind eigene Zeichen.
 *
 * "personal" steht bewusst nicht allein: sonst wuerde auch "Personal &
 * Benefits" - Sport, Kaffee, Zuschuesse - die Aufschluesselung nach Personen
 * aufziehen, und der Betrag käme dann aus den Einzelposten statt aus der
 * Rechnung.
 */
const LOHN_MUSTER = /l[oö]hn|geh[aä]lt|personalkosten|personalaufwand|mitarbeitergeh/i;

const LEER: FixedCostInput = {
  title: '',
  category: '',
  recipient: '',
  reference: '',
  note: '',
  status: 'active',
  cycle: 'monthly',
  amountCents: 0,
  currency: 'EUR',
  // Bei Miete, Löhnen und Steuern denkt man in Bruttobeträgen - das ist der
  // Betrag, der vom Konto geht.
  priceIsNet: false,
  vatRatePercent: 0,
  isVariable: false,
  billingDay: null,
  dueDate: null,
  renewalDate: null,
  cancelBeforeDate: null,
  paymentMethod: 'sepa',
  paymentDetail: '',
  url: '',
  invoiceUrl: '',
  iconSymbol: '',
  iconData: '',
  ownerUid: null,
  positions: [],
};

function centsToInput(cents: number): string {
  return cents === 0 ? '' : (cents / 100).toFixed(2).replace('.', ',');
}

export function FixedCostEditor({
  cost,
  onClose,
  onSaved,
}: {
  cost: FixedCost | null;
  onClose: () => void;
  onSaved: () => void;
}): JSX.Element {
  const { users } = useVault();
  const { busy, error, setError, run } = useAction();

  const [form, setForm] = useState<FixedCostInput>(() => {
    if (!cost) return LEER;
    const { id, createdAt, updatedAt, updatedBy, ...rest } = cost;
    return rest;
  });
  const [betrag, setBetrag] = useState(cost ? centsToInput(cost.amountCents) : '');
  const [iconBusy, setIconBusy] = useState(false);
  const [iconNote, setIconNote] = useState<string | null>(null);

  const patch = <K extends keyof FixedCostInput>(key: K, value: FixedCostInput[K]): void =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const wiederkehrend =
    form.cycle === 'monthly' || form.cycle === 'quarterly' || form.cycle === 'weekly';
  /**
   * null heisst: noch nicht entschieden - dann richtet sich die Aufteilung nach
   * der Kategorie. Wer tippt "Löhne", meint fast immer mehrere Personen; ein
   * Klick genuegt, um es wieder abzustellen.
   */
  const [aufteilungGewaehlt, setAufteilungGewaehlt] = useState<boolean | null>(
    (cost?.positions.length ?? 0) > 0 ? true : null,
  );
  const wirktWieLohn = LOHN_MUSTER.test(form.category) || LOHN_MUSTER.test(form.title);
  const aufschluesseln = aufteilungGewaehlt ?? wirktWieLohn;
  const summeAusPositionen = payrollTotal(form.positions);

  const setzePosition = (index: number, teil: Partial<PayrollPosition>): void =>
    setForm((prev) => ({
      ...prev,
      positions: prev.positions.map((p, i) => (i === index ? { ...p, ...teil } : p)),
    }));
  const istGewerbesteuer = form.category.toLowerCase().includes('gewerbesteuer');
  const vorschau = periodMoney({
    ...form,
    priceCents: parseMoneyToCents(betrag),
    priceIsPerSeat: false,
    seats: null,
    hasCost: true,
    status: 'active',
  } as never);

  async function submit(): Promise<void> {
    if (!form.title.trim()) {
      setError('Bitte einen Namen angeben.');
      return;
    }
    const positionen = aufschluesseln ? form.positions.filter((p) => p.name.trim()) : [];
    const daten: FixedCostInput = {
      ...form,
      positions: positionen,
      amountCents: positionen.length > 0 ? payrollTotal(positionen) : parseMoneyToCents(betrag),
    };
    const ok = await run(async () => {
      if (cost) await api.updateFixedCost(cost.id, daten);
      else await api.createFixedCost(daten);
      return true;
    });
    if (ok) {
      onSaved();
      onClose();
    }
  }

  return (
    <Modal
      wide
      title={cost ? form.title || 'Kostenposten' : 'Neuer Kostenposten'}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose} disabled={busy}>
            Abbrechen
          </button>
          <button className="btn-primary" onClick={() => void submit()} disabled={busy}>
            {busy ? 'Speichern …' : 'Speichern'}
          </button>
        </>
      }
    >
      <ErrorNote error={error} />

      <div className="field">
        <label htmlFor="k-title">Name</label>
        <input
          id="k-title"
          value={form.title}
          onChange={(e) => patch('title', e.target.value)}
          placeholder="z. B. Gewerbesteuer-Vorauszahlung"
          autoFocus
        />
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="k-cat">Kategorie</label>
          <input
            id="k-cat"
            value={form.category}
            onChange={(e) => patch('category', e.target.value)}
            list="fixkategorien"
            placeholder="Löhne, Versicherung, Miete …"
          />
        </div>
        <div className="field">
          <label htmlFor="k-recipient">Empfänger</label>
          <input
            id="k-recipient"
            value={form.recipient}
            onChange={(e) => patch('recipient', e.target.value)}
            placeholder="Finanzamt, Allianz, Vermieter …"
          />
        </div>
        <div className="field">
          <label htmlFor="k-ref">Vertrags- oder Steuernummer</label>
          <input
            id="k-ref"
            value={form.reference}
            onChange={(e) => patch('reference', e.target.value)}
          />
        </div>
      </div>

      <div className="field">
        <label>Symbol</label>
        <div className="row" style={{ marginBottom: 10 }}>
          <ItemIcon item={{ ...form, title: form.title }} size={40} />
          <button
            className="btn-sm"
            disabled={iconBusy || !form.url.trim()}
            title={!form.url.trim() ? 'Dafür braucht es erst eine Webadresse' : undefined}
            onClick={async () => {
              setIconBusy(true);
              setIconNote(null);
              try {
                const { dataUri, host } = await api.fetchIcon(form.url);
                setForm((prev) => ({ ...prev, iconData: dataUri, iconSymbol: '' }));
                setIconNote(`Symbol von ${host} übernommen.`);
              } catch (err) {
                setIconNote(err instanceof Error ? err.message : String(err));
              } finally {
                setIconBusy(false);
              }
            }}
          >
            {iconBusy ? 'Wird geholt …' : 'Vom Anbieter holen'}
          </button>
          {form.iconData || form.iconSymbol ? (
            <button
              className="btn-quiet btn-sm"
              onClick={() => setForm((prev) => ({ ...prev, iconData: '', iconSymbol: '' }))}
            >
              Entfernen
            </button>
          ) : null}
        </div>
        <div className="icon-grid">
          {ICON_SYMBOLS.map((name) => (
            <button
              key={name}
              type="button"
              className="icon-pick"
              aria-pressed={form.iconSymbol === name}
              title={ICON_LABEL[name]}
              onClick={() =>
                setForm((prev) => ({ ...prev, iconSymbol: prev.iconSymbol === name ? '' : name }))
              }
            >
              <SymbolIcon name={name as IconSymbol} />
            </button>
          ))}
        </div>
        {iconNote ? (
          <p className="small muted" style={{ margin: '8px 0 0' }}>
            {iconNote}
          </p>
        ) : null}
      </div>

      <h3 style={{ margin: '22px 0 10px' }}>Betrag und Rhythmus</h3>
      <div className="field-row">
        <div className="field">
          <label htmlFor="k-status">Status</label>
          <select
            id="k-status"
            value={form.status}
            onChange={(e) => patch('status', e.target.value as FixedCostStatus)}
          >
            {(Object.keys(FIXED_STATUS_LABEL) as FixedCostStatus[]).map((k) => (
              <option key={k} value={k}>
                {FIXED_STATUS_LABEL[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="k-cycle">Rhythmus</label>
          <select
            id="k-cycle"
            value={form.cycle}
            onChange={(e) => patch('cycle', e.target.value as FixedCostInput['cycle'])}
          >
            {(['monthly', 'quarterly', 'yearly', 'weekly', 'onetime'] as const).map((c) => (
              <option key={c} value={c}>
                {CYCLE_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="k-amount">Betrag</label>
          <input
            id="k-amount"
            value={aufschluesseln && form.positions.length > 0 ? centsToInput(summeAusPositionen) : betrag}
            onChange={(e) => setBetrag(e.target.value)}
            placeholder="0,00"
            inputMode="decimal"
            disabled={aufschluesseln && form.positions.length > 0}
            title={
              aufschluesseln && form.positions.length > 0
                ? 'Ergibt sich aus den Personen weiter unten'
                : undefined
            }
          />
        </div>
        <div className="field">
          <label htmlFor="k-cur">Währung</label>
          <select
            id="k-cur"
            value={form.currency}
            onChange={(e) => patch('currency', e.target.value)}
          >
            {CURRENCIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="k-netgross">Betrag ist</label>
          <select
            id="k-netgross"
            value={form.priceIsNet ? 'net' : 'gross'}
            onChange={(e) => patch('priceIsNet', e.target.value === 'net')}
          >
            <option value="gross">brutto</option>
            <option value="net">netto</option>
          </select>
        </div>
        <div className="field">
          <label htmlFor="k-vat">Umsatzsteuer</label>
          <select
            id="k-vat"
            value={form.vatRatePercent}
            onChange={(e) => patch('vatRatePercent', Number(e.target.value))}
          >
            {VAT_RATES.map((r) => (
              <option key={r} value={r}>
                {vatLabel(r)}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="small muted" style={{ marginTop: -6, marginBottom: 14 }}>
        Löhne, Steuern und Versicherungen sind umsatzsteuerfrei – dort bleibt 0 % richtig.
      </p>

      <label className="check" style={{ marginBottom: 14 }}>
        <input
          type="checkbox"
          checked={form.isVariable}
          onChange={(e) => patch('isVariable', e.target.checked)}
        />
        Der Betrag schwankt (Strom, Löhne mit Überstunden …)
      </label>

      <div className="field-row">
        {wiederkehrend && form.cycle === 'monthly' ? (
          <div className="field">
            <label htmlFor="k-day">Abbuchung am</label>
            <select
              id="k-day"
              value={form.billingDay ?? ''}
              onChange={(e) => patch('billingDay', e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">– unbekannt –</option>
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => (
                <option key={d} value={d}>
                  {d}. des Monats
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="field">
            <label htmlFor="k-due">Nächste Fälligkeit</label>
            <input
              id="k-due"
              type="date"
              value={form.dueDate ?? ''}
              onChange={(e) => patch('dueDate', e.target.value || null)}
            />
          </div>
        )}
        <div className="field">
          <label htmlFor="k-pay">Zahlungsweg</label>
          <select
            id="k-pay"
            value={form.paymentMethod}
            onChange={(e) => patch('paymentMethod', e.target.value as PaymentMethod)}
          >
            {(Object.keys(PAYMENT_LABEL) as PaymentMethod[]).map((k) => (
              <option key={k} value={k}>
                {PAYMENT_LABEL[k]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="k-paydetail">Näheres dazu</label>
          <input
            id="k-paydetail"
            value={form.paymentDetail}
            onChange={(e) => patch('paymentDetail', e.target.value)}
            placeholder="Konto, Kartennummer …"
          />
        </div>
      </div>

      {istGewerbesteuer && form.cycle === 'quarterly' ? (
        <div className="note" style={{ marginBottom: 14 }}>
          Die Vorauszahlungen sind jeweils zum {GEWERBESTEUER_TERMINE} fällig. Trag den nächsten
          dieser Termine als Fälligkeit ein, dann stimmen alle weiteren automatisch.
        </div>
      ) : null}

      {vorschau.grossCents > 0 ? (
        <p className="small muted">
          {formatMoney(vorschau.grossCents)} {CYCLE_LABEL[form.cycle]}
          {form.vatRatePercent > 0
            ? ` · davon ${formatMoney(vorschau.vatCents)} Umsatzsteuer`
            : ''}
          {form.isVariable ? ' · Betrag schwankt' : ''}
        </p>
      ) : null}

      <h3 style={{ margin: '22px 0 4px' }}>Nach Personen aufschlüsseln</h3>
      <p className="small muted" style={{ marginBottom: 12 }}>
        Sinnvoll bei Löhnen: Der Betrag oben ergibt sich dann aus der Summe und wird nicht mehr von
        Hand gepflegt.
      </p>

      <label className="check" style={{ marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={aufschluesseln}
          onChange={(e) => setAufteilungGewaehlt(e.target.checked)}
        />
        Diesen Posten nach Personen aufteilen
      </label>

      {aufschluesseln ? (
        <>
          <div className="note" style={{ marginBottom: 12 }}>
            <strong>Brutto ist nicht, was ihr zahlt.</strong> Auf den Bruttolohn kommt der
            Arbeitgeberanteil zur Sozialversicherung obendrauf – meist rund{' '}
            {EMPLOYER_SHARE_PERCENT} %. Er wird beim Tippen vorgeschlagen; trag die genaue Zahl aus
            der Lohnabrechnung ein, sobald du sie hast.
            <div className="small" style={{ marginTop: 6 }}>
              Lohnsteuer und Arbeitnehmeranteil stecken bereits im Brutto. Leg dafür{' '}
              <strong>keine</strong> zusätzlichen Posten an – sonst zählst du sie doppelt.
            </div>
          </div>

          <div className="stack">
            {form.positions.map((p, index) => (
              <div className="seat" key={p.id || index} style={{ display: 'block' }}>
                <div className="field-row" style={{ marginBottom: 0 }}>
                  <div className="field">
                    <label>Name</label>
                    <input
                      value={p.name}
                      onChange={(e) => setzePosition(index, { name: e.target.value })}
                      placeholder="Vorname Nachname"
                    />
                  </div>
                  <div className="field">
                    <label>Stelle</label>
                    <input
                      value={p.role}
                      onChange={(e) => setzePosition(index, { role: e.target.value })}
                      placeholder="Grafik, Schnitt …"
                    />
                  </div>
                </div>

                <div className="field-row" style={{ marginBottom: 0 }}>
                  <div className="field">
                    <label>Bruttolohn</label>
                    <input
                      value={p.grossCents === 0 ? '' : centsToInput(p.grossCents)}
                      inputMode="decimal"
                      placeholder="0,00"
                      onChange={(e) => {
                        const brutto = parseMoneyToCents(e.target.value);
                        // Der Arbeitgeberanteil folgt dem Brutto, solange er
                        // nicht von Hand überschrieben wurde.
                        const bisherVorschlag =
                          p.employerCostCents ===
                          Math.round((p.grossCents * EMPLOYER_SHARE_PERCENT) / 100);
                        setzePosition(index, {
                          grossCents: brutto,
                          ...(p.employerCostCents === 0 || bisherVorschlag
                            ? {
                                employerCostCents: Math.round(
                                  (brutto * EMPLOYER_SHARE_PERCENT) / 100,
                                ),
                              }
                            : {}),
                        });
                      }}
                    />
                  </div>
                  <div className="field">
                    <label>Arbeitgeberanteil</label>
                    <input
                      value={p.employerCostCents === 0 ? '' : centsToInput(p.employerCostCents)}
                      inputMode="decimal"
                      placeholder="0,00"
                      onChange={(e) =>
                        setzePosition(index, {
                          employerCostCents: parseMoneyToCents(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="field">
                    <label>Kostet insgesamt</label>
                    <input
                      value={centsToInput(p.grossCents + p.employerCostCents)}
                      disabled
                      style={{ fontWeight: 600 }}
                    />
                  </div>
                </div>

                <div className="row" style={{ marginTop: 4 }}>
                  <input
                    style={{ flex: 1 }}
                    value={p.note}
                    onChange={(e) => setzePosition(index, { note: e.target.value })}
                    placeholder="Notiz – Teilzeit, befristet, Zuschläge …"
                  />
                  <button
                    className="btn-quiet btn-sm"
                    onClick={() =>
                      setForm((prev) => ({
                        ...prev,
                        positions: prev.positions.filter((_, i) => i !== index),
                      }))
                    }
                  >
                    Entfernen
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="row" style={{ marginTop: 12 }}>
            <button
              className="btn-sm"
              onClick={() =>
                setForm((prev) => ({
                  ...prev,
                  positions: [
                    ...prev.positions,
                    { id: '', name: '', role: '', grossCents: 0, employerCostCents: 0, note: '' },
                  ],
                }))
              }
            >
              Person hinzufügen
            </button>
            <span className="spacer" />
            {form.positions.length > 0 ? (
              <span className="small">
                {plural(form.positions.length, 'Person', 'Personen')} ·{' '}
                <strong className="mono">{formatMoney(summeAusPositionen)}</strong>{' '}
                {CYCLE_LABEL[form.cycle]}
              </span>
            ) : null}
          </div>
        </>
      ) : null}

      <h3 style={{ margin: '22px 0 10px' }}>Vertrag und Zuständigkeit</h3>
      <div className="field-row">
        <div className="field">
          <label htmlFor="k-renew">Verlängert sich am</label>
          <input
            id="k-renew"
            type="date"
            value={form.renewalDate ?? ''}
            onChange={(e) => patch('renewalDate', e.target.value || null)}
          />
        </div>
        <div className="field">
          <label htmlFor="k-cancel">Kündigen bis</label>
          <input
            id="k-cancel"
            type="date"
            value={form.cancelBeforeDate ?? ''}
            onChange={(e) => patch('cancelBeforeDate', e.target.value || null)}
          />
        </div>
        <div className="field">
          <label htmlFor="k-owner">Verantwortlich</label>
          <select
            id="k-owner"
            value={form.ownerUid ?? ''}
            onChange={(e) => patch('ownerUid', e.target.value || null)}
          >
            <option value="">– niemand –</option>
            {users.map((u) => (
              <option key={u.uid} value={u.uid}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="field-row">
        <div className="field">
          <label htmlFor="k-url">Webseite</label>
          <input
            id="k-url"
            value={form.url}
            onChange={(e) => patch('url', e.target.value)}
            placeholder="https://…"
          />
        </div>
        <div className="field">
          <label htmlFor="k-invoice">Link zu den Belegen</label>
          <input
            id="k-invoice"
            value={form.invoiceUrl}
            onChange={(e) => patch('invoiceUrl', e.target.value)}
            placeholder="https://…"
          />
        </div>
      </div>

      <div className="field">
        <label htmlFor="k-note">Notiz</label>
        <textarea
          id="k-note"
          value={form.note}
          onChange={(e) => patch('note', e.target.value)}
          placeholder="Besonderheiten, Ansprechpartner, Kündigungsbedingungen …"
        />
      </div>

      <datalist id="fixkategorien">
        {FIXED_CATEGORIES.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
    </Modal>
  );
}
